import { useEffect, useRef } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { subscribeToMemberRowChanges } from 'src/store/workspace/memberRowChanges';

// A CHART MUST NOT WRITE BACK A ROW IT KNOWS ANOTHER TAB HAS CHANGED.
//
// Saving a linked person's details writes their name, date of birth and sex
// onto each linked secondary record, and those live in other families
// (POST /person-links/sync-secondaries). When one of those families is open in
// another workspace tab, that tab's memberRecords still hold the old values,
// and its pedigree autosave $sets every non-proband member's whole row: the
// next edit made there wrote the old name, date of birth and sex back over the
// sync, silently.
//
// The writer announces the person numbers it wrote (memberRowChanges.js).
// Every mounted chart hears it; one that holds none of those rows does nothing
// at all. One that holds any:
//
//   1. holds its autosave at once (runWithAutosaveHeld), so no save can send
//      the stale rows from here on. A save already in flight settles first,
//      and the read below then sees what it wrote.
//   2. reloads the family the way the chart loads it, GET /pedigree/details
//      (without the record-open audit flag: this is a reload after an edit),
//   3. takes ONLY the fields the writer set, onto ONLY the rows it named, with
//      a bare setMemberRecords. Replacing the whole family, as a family load
//      does, would throw away anything this tab has pending, and moving the
//      save status would send a POST nobody asked for.
//   4. releases the hold. A save that was pending goes, once, from the
//      refreshed rows.
//
// If the reload fails, a row it did not return, or every row, takes the
// announced values instead: they are exactly what the server $set, and
// releasing with the stale values still on the row would bring the defect
// straight back.
//
// Rows are matched on the person number the server wrote by: a member's
// `familyMemberId`, or a family root's `proBandId`.

const personNumbersOf = (m) =>
  [m?.familyMemberId, m?.proBandId]
    .filter((id) => id != null && id !== '')
    .map(String);

/** The rows in `records` that a change to `memberIds` touches. */
export const rowsTouchedBy = (records, memberIds) => {
  const wanted = new Set((memberIds || []).map(String));
  return (records || []).filter((m) =>
    personNumbersOf(m).some((id) => wanted.has(id))
  );
};

/**
 * The records with `change.fields` of each touched row taken from the
 * server's copy of that person (`freshRows`, matched by the same person
 * number), or from `change.patch` where there is no server copy. A field the
 * server's copy does not carry was cleared (the details read drops empty
 * values), so it becomes null. Returns `records` itself when nothing changed.
 */
export const withExternalRowChanges = (records, change, freshRows) => {
  if (!Array.isArray(records)) return records;
  const wanted = new Set((change?.memberIds || []).map(String));
  const fields = change?.fields || [];
  const freshByNumber = new Map();
  (freshRows || []).forEach((row) =>
    personNumbersOf(row).forEach((id) => {
      if (!freshByNumber.has(id)) freshByNumber.set(id, row);
    })
  );

  let changed = false;
  const next = records.map((m) => {
    const id = personNumbersOf(m).find((n) => wanted.has(n));
    if (id === undefined) return m;
    const fresh = freshByNumber.get(id);
    const update = {};
    fields.forEach((field) => {
      update[field] = fresh ? fresh[field] ?? null : change.patch?.[field];
    });
    if (fields.every((field) => (m[field] ?? null) === (update[field] ?? null)))
      return m;
    changed = true;
    return { ...m, ...update };
  });
  return changed ? next : records;
};

/**
 * @param {object} args
 * @param {string} args.familyId   the family this chart shows
 * @param {object} args.rowSync    PedigreeChart's MemberRowSyncContext value
 * @param {boolean} [args.disabled] a read-only preview: nothing to protect
 */
export function useExternalMemberRowRefresh({
  familyId,
  rowSync,
  disabled = false
}) {
  const latest = useRef({ familyId, rowSync, disabled });
  latest.current = { familyId, rowSync, disabled };

  useEffect(
    () =>
      subscribeToMemberRowChanges((change) => {
        const { familyId: id, rowSync: sync, disabled: off } = latest.current;
        if (off || !id || !sync) return;
        if (!rowsTouchedBy(sync.getMemberRecords(), change.memberIds).length) {
          return;
        }
        sync
          .runWithAutosaveHeld(
            async () => {
              let freshRows = null;
              try {
                const { data } = await axios.get(
                  APICONSTANTS.getPedigreeDetails.replace(':familyId', id)
                );
                freshRows = [data?.proband, ...(data?.families || [])].filter(
                  Boolean
                );
              } catch {
                freshRows = null;
              }
              sync.setMemberRecords((prev) =>
                withExternalRowChanges(prev, change, freshRows)
              );
            },
            { reason: `external-row-change:${change.source || 'unknown'}` }
          )
          .catch((err) => {
            console.error('Failed to refresh externally changed rows:', err);
          });
      }),
    []
  );
}
